import { useState, useEffect, useCallback } from "react";
import { Plus, X, Mail, BookmarkPlus, BookmarkCheck, BookUser, Pencil, Check, Phone } from "lucide-react";
import apiClient from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatPhoneNumber } from "../../lib/formatters";
import { STANDARD_POSITIONS } from "../../lib/jobContacts";
import ContactFormDialog from "../contacts/ContactFormDialog";
import ContactPickerDialog, { pickedPhone } from "../contacts/ContactPickerDialog";
import CompanyPicker from "../contacts/CompanyPicker";
import { useContactPositions } from "../contacts/PositionPicker";

const OTHER = "__other__";
const isStandard = (label) => STANDARD_POSITIONS.some((p) => p.label === label);
// Map a shared-contact title onto a job position: exact/loose match to a standard label, else keep the title as a custom position.
const positionFromTitle = (title) => {
  const t = (title || "").trim();
  if (!t) return "";
  const lower = t.toLowerCase();
  const hit = STANDARD_POSITIONS.find((p) => p.label.toLowerCase() === lower)
    || STANDARD_POSITIONS.find((p) => lower.includes(p.label.toLowerCase()))
    || (/\b(pm|proj(ect)? mgr)\b/.test(lower) ? STANDARD_POSITIONS[2] : null)
    || (/\bsuper\b/.test(lower) ? STANDARD_POSITIONS[1] : null);
  return hit ? hit.label : t;
};

const PositionField = ({ value, onChange, testId, extraPositions = [] }) => {
  const extras = extraPositions.filter((p) => !isStandard(p));
  const known = (label) => isStandard(label) || extras.includes(label);
  const [custom, setCustom] = useState(!!value && !known(value));
  useEffect(() => { if (value) setCustom(!known(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  // Derive synchronously too: passing a non-item value to Radix Select makes it fire onValueChange("") and wipe the position.
  const showCustom = custom || (!!value && !known(value));
  const selectValue = showCustom ? OTHER : value || "";
  return (
    <div className="space-y-1">
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === OTHER) { setCustom(true); onChange(""); }
          else { setCustom(false); onChange(v); }
        }}
      >
        <SelectTrigger data-testid={`${testId}-position`}><SelectValue placeholder="Position" /></SelectTrigger>
        <SelectContent>
          {STANDARD_POSITIONS.map((p) => <SelectItem key={p.key} value={p.label}>{p.label}</SelectItem>)}
          {extras.map((p) => <SelectItem key={`x-${p}`} value={p}>{p}</SelectItem>)}
          <SelectItem value={OTHER}>Other…</SelectItem>
        </SelectContent>
      </Select>
      {showCustom && (
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Type position (e.g. Accounts Payable)" autoFocus data-testid={`${testId}-position-custom`} />
      )}
    </div>
  );
};

// Per-field suggestions (native datalist): shared Contacts + people used on other jobs. Picking fills only this field.
const SuggestList = ({ id, options }) => (
  <datalist id={id} data-testid={`${id}-list`}>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label || undefined}</option>)}
  </datalist>
);

const useContactSuggestions = () => {
  const [s, setS] = useState({ names: [], emails: [], phones: [], shared_emails: [] });
  const reload = useCallback(() => { apiClient.get("/shared-contacts/suggestions").then((r) => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => { reload(); }, [reload]);
  return [s, reload];
};

// Job contact row → prefilled shared-contact form (name split, position as title, phone in Office — swap in the form).
const prefillFromRow = (c) => {
  const parts = (c.name || "").trim().split(/\s+/).filter(Boolean);
  return { first_name: parts[0] || "", last_name: parts.slice(1).join(" "), company: c.company || "", title: c.position || "", email: (c.email || "").trim(), phone_office: c.phone || "" };
};

// Flat, add-as-you-go contact list. Any number of people per position.
// Rows marked "done" render as permanent read-only cards with an Edit button; new rows start in edit mode.
const JobContactsEditor = ({ contacts, onAdd, onUpdate, onRemove, defaultCompany = "" }) => {
  const emails = contacts.map((c) => (c.email || "").trim()).filter((e) => e.includes("@"));
  const [suggest, reloadSuggest] = useContactSuggestions();
  const { positions: contactPositions } = useContactPositions();
  const [saveTarget, setSaveTarget] = useState(null);
  const [pickFor, setPickFor] = useState(null);
  // Rows with a name render read-only unless explicitly opened for editing; nameless (new) rows are always editable.
  const [editingIds, setEditingIds] = useState(() => new Set());
  useEffect(() => {
    const fresh = contacts.filter((c) => !(c.name || "").trim() && !editingIds.has(c.id)).map((c) => c.id);
    if (fresh.length) setEditingIds((s) => new Set([...s, ...fresh]));
  }, [contacts, editingIds]);
  const isDone = (c) => !!(c.name || "").trim() && !editingIds.has(c.id);
  const markDone = (id, v) => setEditingIds((s) => { const n = new Set(s); v ? n.delete(id) : n.add(id); return n; });
  const pick = (sc) => {
    const row = contacts.find((c) => c.id === pickFor);
    const pos = positionFromTitle(sc.title);
    if (pos && !(row?.position || "").trim()) onUpdate(pickFor, "position", pos);
    onUpdate(pickFor, "name", sc.display_name || "");
    onUpdate(pickFor, "company", sc.company || "");
    onUpdate(pickFor, "email", sc.email || "");
    onUpdate(pickFor, "phone", formatPhoneNumber(pickedPhone(sc)));
  };
  const inShared = new Set((suggest.shared_emails || []).map((e) => e.toLowerCase()));
  const SaveOrInContacts = ({ c, i }) => (c.email && inShared.has(c.email.trim().toLowerCase()) ? (
    <span className="h-9 inline-flex items-center gap-1 text-xs text-emerald-700 px-1 whitespace-nowrap" title="Already in shared Contacts" data-testid={`contact-row-${i}-in-contacts`}>
      <BookmarkCheck className="w-4 h-4" /> In Contacts
    </span>
  ) : (
    <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-sky-700" onClick={() => setSaveTarget(prefillFromRow(c))} disabled={!(c.name || c.email)} title="Save to shared Contacts" data-testid={`contact-row-${i}-save-to-contacts`}>
      <BookmarkPlus className="w-4 h-4" />
    </Button>
  ));
  const rowIndex = new Map(contacts.map((c, i) => [c.id, i]));
  const byPos = {};
  for (const c of contacts) (byPos[(c.position || "").trim()] = byPos[(c.position || "").trim()] || []).push(c);
  const customLabels = Object.keys(byPos).filter((l) => l && !isStandard(l)).sort((a, b) => a.localeCompare(b));
  const groups = [...STANDARD_POSITIONS.map((p) => p.label), ...customLabels].map((label) => ({ label, rows: byPos[label] || [] }));
  const unassigned = byPos[""] || [];

  const renderRow = (c, { showPosition = false } = {}) => {
    const i = rowIndex.get(c.id);
    return isDone(c) ? (
      <div key={c.id} className="grid grid-cols-1 md:grid-cols-[1.15fr_0.9fr_1.5fr_auto] gap-3 items-center rounded-lg border border-border bg-muted/30 px-3 py-2" data-testid={`contact-row-${i}`}>
        <div className="min-w-0"><p className="font-medium truncate" data-testid={`contact-row-${i}-name-view`}>{c.name}</p>{c.company && <p className="text-xs text-muted-foreground truncate" data-testid={`contact-row-${i}-company-view`}>{c.company}</p>}</div>
        <p className="text-sm text-muted-foreground truncate inline-flex items-center gap-1.5" data-testid={`contact-row-${i}-phone-view`}>
          {c.phone && <><Phone className="w-3.5 h-3.5 shrink-0" />{c.phone}</>}
        </p>
        <p className="text-sm text-muted-foreground truncate inline-flex items-center gap-1.5" data-testid={`contact-row-${i}-email-view`}>
          {c.email && <><Mail className="w-3.5 h-3.5 shrink-0" />{c.email}</>}
        </p>
        <div className="flex items-center gap-1 justify-end">
          <SaveOrInContacts c={c} i={i} />
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => markDone(c.id, false)} data-testid={`contact-row-${i}-edit`}>
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onRemove(c.id)} title="Remove contact" data-testid={`contact-row-${i}-remove`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    ) : (
      <div key={c.id} className={`grid grid-cols-1 gap-3 items-start rounded-lg border border-dashed border-border p-2 ${showPosition ? "md:grid-cols-[1fr_1fr_1fr_0.9fr_1.1fr_auto_auto_auto_auto]" : "md:grid-cols-[1.1fr_1fr_0.9fr_1.2fr_auto_auto_auto_auto]"}`} data-testid={`contact-row-${i}`}>
        {showPosition && <PositionField value={c.position} onChange={(v) => onUpdate(c.id, "position", v)} testId={`contact-row-${i}`} extraPositions={contactPositions} />}
        <Input value={c.name} onChange={(e) => onUpdate(c.id, "name", e.target.value)} placeholder="Name" list="contact-suggest-names" autoComplete="off" data-testid={`contact-row-${i}-name`} />
        <CompanyPicker value={c.company || ""} onChange={(v) => onUpdate(c.id, "company", v)} placeholder="Company" testId={`contact-row-${i}-company`} />
        <Input value={c.phone} onChange={(e) => onUpdate(c.id, "phone", formatPhoneNumber(e.target.value))} placeholder="Phone" maxLength={14} list="contact-suggest-phones" autoComplete="off" data-testid={`contact-row-${i}-phone`} />
        <Input type="email" value={c.email} onChange={(e) => onUpdate(c.id, "email", e.target.value)} placeholder="Email" list="contact-suggest-emails" autoComplete="off" data-testid={`contact-row-${i}-email`} />
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-slate-600" onClick={() => setPickFor(c.id)} title="Pick from shared Contacts" data-testid={`contact-row-${i}-pick`}>
          <BookUser className="w-4 h-4" />
        </Button>
        <SaveOrInContacts c={c} i={i} />
        <Button type="button" variant="default" size="icon" className="h-9 w-9" onClick={() => markDone(c.id, true)} disabled={!c.name} title="Done" data-testid={`contact-row-${i}-done`}>
          <Check className="w-4 h-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-600" onClick={() => onRemove(c.id)} title="Remove contact" data-testid={`contact-row-${i}-remove`}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  const slug = (l) => l.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return (
    <div className="space-y-4" data-testid="job-contacts-editor">
      <ContactFormDialog open={!!saveTarget} onOpenChange={(o) => { if (!o) setSaveTarget(null); }} contact={saveTarget} onSaved={reloadSuggest} />
      <ContactPickerDialog open={!!pickFor} onOpenChange={(o) => { if (!o) setPickFor(null); }} onPick={pick} />
      <SuggestList id="contact-suggest-names" options={suggest.names} />
      <SuggestList id="contact-suggest-emails" options={suggest.emails} />
      <SuggestList id="contact-suggest-phones" options={suggest.phones} />
      {groups.map(({ label, rows }) => (
        <section key={label} className="space-y-2" data-testid={`contact-group-${slug(label)}`}>
          <div className="flex items-center justify-between gap-2 border-b border-border pb-1">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700 flex items-center gap-2" data-testid={`contact-group-${slug(label)}-title`}>
              {label}{rows.length > 0 && <span className="text-xs font-normal normal-case tracking-normal text-muted-foreground">({rows.length})</span>}
            </h4>
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs text-sky-700" onClick={() => onAdd(label, defaultCompany)} data-testid={`contact-group-${slug(label)}-add`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
          {rows.length === 0 ? <p className="text-xs text-muted-foreground pl-1" data-testid={`contact-group-${slug(label)}-empty`}>No one yet</p> : rows.map((c) => renderRow(c))}
        </section>
      ))}
      {unassigned.length > 0 && (
        <section className="space-y-2" data-testid="contact-group-unassigned">
          <div className="border-b border-border pb-1">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-amber-700">Choose a position</h4>
          </div>
          {unassigned.map((c) => renderRow(c, { showPosition: true }))}
        </section>
      )}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={() => onAdd("", defaultCompany)} data-testid="add-contact-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Other Position
        </Button>
        {emails.length > 1 && (
          <Button type="button" variant="ghost" size="sm" onClick={() => { window.location.href = `mailto:${emails.join(",")}`; }} data-testid="email-all-contacts-btn">
            <Mail className="w-4 h-4 mr-1" /> Email all ({emails.length})
          </Button>
        )}
        {(suggest.names.length > 0) && (
          <span className="text-xs text-muted-foreground" data-testid="contact-suggestions-hint">Start typing — names, phones and emails from Contacts and other jobs are suggested.</span>
        )}
      </div>
    </div>
  );
};

export default JobContactsEditor;
